import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { ExclamationIcon, PencilIcon, TruckIcon } from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/solid';
import { courtesyContentStringAtom } from '@service/bugo/containers/MemberCourtesy/courtesy.atom';
import { getCourtesyStringFromContent } from '@service/bugo/containers/MemberCourtesy/courtesyGenerator';
import { useGiftAuthGate } from '@service/giftStore/components/GiftStoreAuthGate';
import { GiftCouponShareModal } from '@service/giftStore/containers/GiftCouponShareModal/GiftCouponShareModal';
import { bugoDataAtomFamily, bugoStatusAtomFamily } from '@shared/api/bugo/bugo.atom';
import { useBugoHook } from '@shared/api/bugo/bugo.hook';
import {
  courtesyContentListByMemberDataAtom,
  courtesyContentListByMemberStatusAtom,
} from '@shared/api/courtesyAsset/courtesyAsset.atom';
import { useCourtesyContentListByMemberHook } from '@shared/api/courtesyAsset/courtesyAsset.hook';
import { shareCoupon } from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import {
  giftOrderForStoreDataAtomFamily,
  giftOrderForStoreStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import { updateGiftOrderCourtesy } from '@shared/api/giftOrder/giftOrder.controller';
import { useGiftOrderForStoreHook } from '@shared/api/giftOrder/giftOrder.hook';
import {
  GiftOrderItem,
  GiftOrderItemType,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatPrice } from '@shared/utils/formatPrice';
import { useParams } from 'react-router-dom';

import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';

// 공유 상태 색상
const getShareStatusStyle = (status: GiftCouponShareStatus) => {
  switch (status) {
    case GiftCouponShareStatus.NotShared:
      return 'bg-gray-100 text-gray-600';
    case GiftCouponShareStatus.Shared:
      return 'bg-green-100 text-green-700';
    case GiftCouponShareStatus.Viewed:
      return 'bg-blue-100 text-blue-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

// 공유 상태 라벨
const getShareStatusLabel = (status: GiftCouponShareStatus) => {
  switch (status) {
    case GiftCouponShareStatus.NotShared:
      return '미전달';
    case GiftCouponShareStatus.Shared:
      return '전달완료';
    case GiftCouponShareStatus.Viewed:
      return '확인됨';
    default:
      return status;
  }
};

// 컴팩트 쿠폰 행 컴포넌트
const CouponRow: FC<{
  coupon: GiftCouponForStore;
  onShare: (coupon: GiftCouponForStore) => void;
  isSharing: boolean;
}> = ({ coupon, onShare, isSharing }) => {
  const giftItem = coupon.giftItem;
  const giftItemInfo = typeof giftItem === 'object' ? giftItem?.giftItemInfo : null;

  // 이미지 URL
  const imageUrl = useMemo(() => {
    if (typeof giftItem === 'object') {
      return giftItemInfo?.image?.url || giftItem?.image?.url || '';
    }
    return '';
  }, [giftItem, giftItemInfo]);

  // 공유 여부
  const isShared = coupon.shareStatus !== GiftCouponShareStatus.NotShared;

  // 교환 완료 여부
  const isRedeemed =
    coupon.redeemStatus === GiftCouponRedeemStatus.Delivered ||
    coupon.redeemStatus === GiftCouponRedeemStatus.Refunded ||
    coupon.redeemStatus === GiftCouponRedeemStatus.Canceled;

  // 상품명
  const itemName =
    typeof giftItem === 'object' ? giftItem?.name || giftItemInfo?.name : '';

  return (
    <div
      className={`flex items-center gap-3 rounded-lg bg-white p-3 shadow-sm ${
        isShared ? 'ring-1 ring-green-400' : ''
      }`}
    >
      {/* 이미지 */}
      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
        {imageUrl ? (
          <img
            src={fileBucketUrl(imageUrl, 'w200')}
            alt={itemName || '쿠폰'}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
            No Image
          </div>
        )}
      </div>

      {/* 정보 */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">#{coupon.seq}</span>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${getShareStatusStyle(
              coupon.shareStatus,
            )}`}
          >
            {getShareStatusLabel(coupon.shareStatus)}
          </span>
        </div>
        <p className="mt-0.5 truncate text-sm font-medium text-gray-800">{itemName}</p>
      </div>
      {/* 공유 버튼 */}
      {!isRedeemed && (
        <button
          onClick={() => onShare(coupon)}
          disabled={isSharing}
          className={`flex-shrink-0 rounded-xl px-5 py-2.5 text-sm font-bold transition ${
            isShared
              ? 'bg-green-100 text-green-700'
              : 'bg-purple-600 text-white active:bg-purple-700'
          } ${isSharing ? 'opacity-50' : ''}`}
        >
          {isSharing ? '...' : isShared ? '다시 전달' : '전달하기'}
        </button>
      )}
    </div>
  );
};

// 배송형 상품 행 컴포넌트
const DeliveryRow: FC<{
  item: GiftOrderItem<GiftItemForStore, Image>;
}> = ({ item }) => {
  const imageUrl = useMemo(() => {
    if (typeof item.giftItem === 'object') {
      return item.giftItem?.giftItemInfo?.image?.url || item.giftItem?.image?.url || '';
    }
    return '';
  }, [item]);

  return (
    <div className="flex items-center gap-3 rounded-lg bg-white p-3 shadow-sm">
      {/* 이미지 */}
      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
        {imageUrl ? (
          <img
            src={fileBucketUrl(imageUrl, 'w200')}
            alt={item.giftItemEmbed.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
            No Image
          </div>
        )}
      </div>

      {/* 정보 */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <TruckIcon className="h-3 w-3 text-blue-500" />
          <span className="text-[10px] font-medium text-blue-500">배송형</span>
          <span className="text-xs text-gray-400">x{item.quantity}</span>
        </div>
        <p className="mt-0.5 truncate text-sm font-medium text-gray-800">
          {item.giftItemEmbed.name}
        </p>
      </div>

      {/* 가격 */}
      <span className="flex-shrink-0 text-sm font-bold text-red-500">
        {formatPrice(item.giftItemEmbed.priceRetail * item.quantity)}원
      </span>
    </div>
  );
};

const SHARE_GUIDE_DISMISSED_KEY = 'gift-coupon-share-guide-dismissed';

const ShareGuideModal: FC<{
  open: boolean;
  onConfirm: () => void;
  onClose: () => void;
}> = ({ open, onConfirm, onClose }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleConfirm = useCallback(() => {
    if (dontShowAgain) {
      localStorage.setItem(SHARE_GUIDE_DISMISSED_KEY, 'true');
    }
    onConfirm();
  }, [dontShowAgain, onConfirm]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <ExclamationIcon className="h-6 w-6 text-amber-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-800">안내</h3>
        </div>

        <p className="mb-3 break-keep text-sm leading-relaxed text-gray-700">
          한 번에 여러 명에게 공유하시면{' '}
          <span className="font-bold text-red-500">
            같은 코드가 중복 발송되어 문제가 발생
          </span>
          할 수 있습니다.
        </p>
        <p className="mb-3 break-keep text-sm leading-relaxed text-gray-700">
          전달 시에는 꼭{' '}
          <span className="font-bold text-purple-600">한 번에 한 명씩</span> 전달해
          주세요.
        </p>

        <label className="mb-5 flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          <span className="text-sm text-gray-500">다시 보지 않기</span>
        </label>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-300 py-3 text-sm font-medium text-gray-600 active:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 rounded-xl bg-purple-600 py-3 text-sm font-bold text-white active:bg-purple-700"
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
};

const GiftOrderCouponPage: FC = function GiftOrderCouponPage() {
  const { orderId } = useParams<{ orderId: string }>();

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);
  const { storeRole } = useAuth();
  const { requestReauth } = useGiftAuthGate();

  // 주문 조회 (coupons가 virtual로 함께 옴)
  const role = storeRole();
  useGiftOrderForStoreHook(orderId ?? '', role);
  const orderStatus = useRecoilValue(giftOrderForStoreStatusAtomFamily(orderId ?? ''));
  const order = useRecoilValue(giftOrderForStoreDataAtomFamily(orderId ?? ''));

  // bugo 조회 (order.bugo로)
  const bugoId = order?.bugo ?? '';
  useBugoHook(bugoId, !bugoId);
  const bugoStatus = useRecoilValue(bugoStatusAtomFamily(bugoId));
  const bugo = useRecoilValue(bugoDataAtomFamily(bugoId));

  // 답례글 목록 조회 (bugo.member._id로)
  const memberId = bugo?.member?._id ?? '';
  useCourtesyContentListByMemberHook(memberId);
  const courtesyContentListStatus = useRecoilValue(courtesyContentListByMemberStatusAtom);
  const courtesyContentList = useRecoilValue(courtesyContentListByMemberDataAtom);

  // 전역 답례글 atom (다른 페이지와 공유)
  const [savedCourtesyString, setSavedCourtesyString] = useRecoilState(
    courtesyContentStringAtom,
  );
  // giftOrder 데이터 업데이트용
  const setOrderData = useSetRecoilState(giftOrderForStoreDataAtomFamily(orderId ?? ''));

  // 로컬 답례글 상태
  const [courtesyContent, setCourtesyContent] = useState('');
  const [selectedContentIndex, setSelectedContentIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [originalContent, setOriginalContent] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 초기 답례글 설정 (우선순위: order.courtesyContent > savedCourtesyString > 템플릿)
  // 한 번만 초기화하고, 이후에는 사용자 수정을 허용
  useEffect(() => {
    // 이미 초기화되었으면 스킵
    if (isInitialized) return;

    // 1. order에 저장된 답례글이 있으면 사용
    if (order?.courtesyContent) {
      setCourtesyContent(order.courtesyContent);
      setOriginalContent(order.courtesyContent);
      setSavedCourtesyString(order.courtesyContent);
      setIsInitialized(true);
      return;
    }

    // 2. 전역 atom에 저장된 답례글이 있으면 사용
    if (savedCourtesyString) {
      setCourtesyContent(savedCourtesyString);
      setOriginalContent(savedCourtesyString);
      setIsInitialized(true);
      return;
    }

    // 3. bugo와 답례글 목록이 로드되면 기본 답례글 생성
    if (bugo && courtesyContentList && courtesyContentList.length > 0) {
      const defaultContentId = bugo.fevent?.courtesyContent;
      const defaultContent = defaultContentId
        ? courtesyContentList.find((c) => c._id === defaultContentId)
        : courtesyContentList[0];

      if (defaultContent) {
        const generatedContent = getCourtesyStringFromContent(defaultContent, bugo);
        setCourtesyContent(generatedContent);
        setOriginalContent(generatedContent);
        setSavedCourtesyString(generatedContent);

        const index = courtesyContentList.findIndex((c) => c._id === defaultContent._id);
        setSelectedContentIndex(index >= 0 ? index : 0);
        setIsInitialized(true);
      }
    }
  }, [
    isInitialized,
    order?.courtesyContent,
    bugo,
    courtesyContentList,
    savedCourtesyString,
    setSavedCourtesyString,
  ]);

  // 답례글 선택 핸들러
  const handleContentSelect = useCallback(
    (index: number) => {
      if (!courtesyContentList || !bugo) return;

      const selected = courtesyContentList[index];
      if (selected) {
        const generatedContent = getCourtesyStringFromContent(selected, bugo);
        setCourtesyContent(generatedContent);
        setSavedCourtesyString(generatedContent);
        setSelectedContentIndex(index);
        setIsModified(generatedContent !== originalContent);
      }
    },
    [courtesyContentList, bugo, setSavedCourtesyString, originalContent],
  );

  // 답례글 직접 수정 핸들러
  const handleContentChange = useCallback(
    (value: string) => {
      setCourtesyContent(value);
      setSavedCourtesyString(value);
      setIsModified(value !== originalContent);
    },
    [setSavedCourtesyString, originalContent],
  );

  // textarea auto-resize
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  useEffect(() => {
    if (isEditing) {
      adjustTextareaHeight();
    }
  }, [isEditing, courtesyContent, adjustTextareaHeight]);

  // 답례글 저장 핸들러
  const handleSaveCourtesy = useCallback(async () => {
    if (!orderId || !courtesyContent) return;

    try {
      setIsSaving(true);
      const updatedOrder = await updateGiftOrderCourtesy(
        orderId,
        { courtesyContent },
        role,
      );
      // 로컬 상태 업데이트
      setOrderData((prev) =>
        prev ? { ...prev, courtesyContent: updatedOrder.courtesyContent } : prev,
      );
      setOriginalContent(courtesyContent);
      setIsModified(false);
      setIsEditing(false);
      alert('답례글이 저장되었습니다.');
    } catch (error) {
      console.error('답례글 저장 실패:', error);
      alert('답례글 저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  }, [orderId, courtesyContent, role, setOrderData]);

  useEffect(() => {
    setTitle('답례품 전달');
  }, [setTitle]);

  // 쿠폰 목록 (order.coupons에서 가져옴)
  const couponList = useMemo(() => {
    return (order?.coupons ?? []) as GiftCouponForStore[];
  }, [order]);

  // 배송형 상품 분리
  const deliveryItems = useMemo(() => {
    if (!order?.orderDetails) return [];
    return order.orderDetails.filter(
      (item) => item.type === GiftOrderItemType.Delivery,
    ) as GiftOrderItem<GiftItemForStore, Image>[];
  }, [order]);

  // 공유 모달 관련 상태
  const shareModalOpenId = `gift-coupon-share-${orderId}`;
  const setShareModalOpen = useSetRecoilState(simpleOpenAtomFamily(shareModalOpenId));
  const [currentShareCoupon, setCurrentShareCoupon] = useState<GiftCouponForStore | null>(
    null,
  );

  // 공유 링크 생성
  const currentShareUrl = useMemo(() => {
    if (!currentShareCoupon) return '';
    return `${window.location.origin}/gift-store/coupon/${currentShareCoupon.code}`;
  }, [currentShareCoupon]);

  // 공유 모달 열기 핸들러
  const handleOpenShareModal = useCallback(
    (coupon: GiftCouponForStore) => {
      setCurrentShareCoupon(coupon);
      setShareModalOpen(true);
    },
    [setShareModalOpen],
  );

  // 공유 완료 핸들러 (모달에서 공유 시 호출)
  const handleShareComplete = useCallback(async () => {
    if (!currentShareCoupon) return;

    try {
      await shareCoupon(currentShareCoupon._id, role);
      // 목록 업데이트를 위해 order 데이터 갱신
      setOrderData((prev) => {
        if (!prev || !prev.coupons) return prev;
        return {
          ...prev,
          coupons: prev.coupons.map((c) =>
            c._id === currentShareCoupon._id
              ? { ...c, shareStatus: GiftCouponShareStatus.Shared }
              : c,
          ),
        };
      });
    } catch (error) {
      console.error('공유 상태 업데이트 실패:', error);
    }
  }, [currentShareCoupon, role, setOrderData]);

  // 단일 쿠폰 공유 핸들러
  const handleShare = useCallback(
    (coupon: GiftCouponForStore) => {
      // 공유 모달 열기
      handleOpenShareModal(coupon);
    },
    [handleOpenShareModal],
  );

  // 하단 버튼 안내 모달 상태
  const [showShareGuide, setShowShareGuide] = useState(false);
  const pendingShareRef = useRef<GiftCouponForStore | null>(null);

  const handleBottomButtonClick = useCallback(() => {
    const firstUnshared = couponList.find(
      (c) => c.shareStatus === GiftCouponShareStatus.NotShared,
    );
    if (!firstUnshared) return;

    const dismissed = localStorage.getItem(SHARE_GUIDE_DISMISSED_KEY) === 'true';
    if (dismissed) {
      handleShare(firstUnshared);
    } else {
      pendingShareRef.current = firstUnshared;
      setShowShareGuide(true);
    }
  }, [couponList, handleShare]);

  const handleShareGuideConfirm = useCallback(() => {
    setShowShareGuide(false);
    if (pendingShareRef.current) {
      handleShare(pendingShareRef.current);
      pendingShareRef.current = null;
    }
  }, [handleShare]);

  // 주문 조회 실패(권한 없음 등) → 즉시 재로그인 폼으로 전환
  useEffect(() => {
    if (orderStatus === 'error' || (orderStatus === 'success' && !order)) {
      requestReauth();
    }
  }, [orderStatus, order, requestReauth]);

  // 로딩 중 또는 에러(재로그인 전환 대기)
  if (
    orderStatus === 'loading' ||
    orderStatus === 'idle' ||
    orderStatus === 'error' ||
    !order
  ) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  // 통계 계산
  const totalCoupons = couponList.length;
  const sharedCoupons = couponList.filter(
    (c) => c.shareStatus !== GiftCouponShareStatus.NotShared,
  ).length;
  const unsharedCoupons = totalCoupons - sharedCoupons;

  return (
    <div
      className={`min-h-screen bg-gradient-to-b from-purple-50 to-white pt-4 ${
        unsharedCoupons > 0 ? 'pb-32' : 'pb-8'
      }`}
    >
      {/* 쿠폰이 있는 경우 */}
      {couponList.length > 0 && (
        <>
          {/* STEP 1: 답례글 확인 */}
          <div className="mx-4 mb-4">
            <div className="mb-2 flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-600 text-xs font-bold text-white">
                1
              </span>
              <h3 className="text-base font-bold text-gray-800">답례글 확인</h3>
            </div>

            <div className="rounded-xl bg-white shadow-md">
              {!isEditing ? (
                <div className="px-4 py-4">
                  {courtesyContent ? (
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                      {courtesyContent}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400">답례글이 아직 없습니다.</p>
                  )}
                  <button
                    onClick={() => setIsEditing(true)}
                    className="mt-3 flex items-center gap-1.5 rounded-lg border border-purple-200 bg-purple-50 px-4 py-2 text-sm font-medium text-purple-700 active:bg-purple-100"
                  >
                    <PencilIcon className="h-4 w-4" />
                    답례글 수정
                  </button>
                </div>
              ) : (
                <div className="px-4 pb-4 pt-3">
                  {/* 답례글 템플릿 선택 */}
                  {courtesyContentListStatus === 'success' &&
                    courtesyContentList &&
                    courtesyContentList.length > 0 && (
                      <div className="mb-3">
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          답례글 템플릿
                        </label>
                        <select
                          value={selectedContentIndex}
                          onChange={(e) => handleContentSelect(Number(e.target.value))}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-400"
                        >
                          {courtesyContentList.map((content, index) => (
                            <option key={content._id} value={index}>
                              {content.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                  {/* 로딩 중 */}
                  {(courtesyContentListStatus === 'loading' ||
                    courtesyContentListStatus === 'idle') &&
                    bugoStatus !== 'error' && (
                      <div className="mb-3 text-center text-xs text-gray-400">
                        답례글 목록을 불러오는 중...
                      </div>
                    )}

                  {/* 답례글 편집 */}
                  <textarea
                    ref={textareaRef}
                    value={courtesyContent}
                    onChange={(e) => {
                      handleContentChange(e.target.value);
                      adjustTextareaHeight();
                    }}
                    placeholder="답례글을 입력하세요..."
                    className="min-h-[160px] w-full resize-none overflow-hidden rounded-lg border border-gray-200 p-3 text-sm leading-relaxed focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-400"
                  />

                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => {
                        if (isModified) {
                          setCourtesyContent(originalContent);
                          setSavedCourtesyString(originalContent);
                          setIsModified(false);
                        }
                        setIsEditing(false);
                      }}
                      className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-600 active:bg-gray-50"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleSaveCourtesy}
                      disabled={isSaving || !isModified}
                      className={`flex-1 rounded-lg py-2.5 text-sm font-bold transition ${
                        isModified
                          ? 'bg-purple-600 text-white active:bg-purple-700'
                          : 'bg-gray-100 text-gray-400'
                      } ${isSaving ? 'opacity-50' : ''}`}
                    >
                      {isSaving ? '저장 중...' : isModified ? '저장하기' : '저장됨'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* STEP 2: 답례품 전달 */}
          <div className="mx-4">
            <div className="mb-2 flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-600 text-xs font-bold text-white">
                2
              </span>
              <h3 className="text-base font-bold text-gray-800">
                답례품을 답례글과 함께 전달
              </h3>
            </div>

            {/* 전달 현황 */}
            <div className="mb-3 rounded-xl bg-white p-3 shadow-md">
              <div className="flex items-center justify-center gap-6">
                <div className="text-center">
                  <p className="text-xl font-bold text-purple-600">{totalCoupons}</p>
                  <p className="text-xs text-gray-500">전체</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-green-600">{sharedCoupons}</p>
                  <p className="text-xs text-gray-500">전달</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-gray-400">{unsharedCoupons}</p>
                  <p className="text-xs text-gray-500">미전달</p>
                </div>
              </div>

              {/* 모두 전달 완료 */}
              {unsharedCoupons === 0 && totalCoupons > 0 && (
                <div className="mt-3 flex items-center justify-center gap-2 rounded-lg bg-green-100 py-2.5 text-green-700">
                  <CheckCircleSolidIcon className="h-5 w-5" />
                  <span className="font-bold">모두 전달 완료!</span>
                </div>
              )}
            </div>

            {/* 쿠폰 목록 */}
            <p className="mb-2 text-xs text-gray-500">
              아래 전달하기 버튼을 누르면 위 답례글과 함께 전달됩니다.
            </p>
            <div className="space-y-2">
              {couponList.map((coupon) => (
                <CouponRow
                  key={coupon._id}
                  coupon={coupon}
                  onShare={handleShare}
                  isSharing={false}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* 쿠폰이 없고 배송형만 있는 경우 */}
      {couponList.length === 0 && deliveryItems.length > 0 && (
        <div className="px-4 text-center">
          <h2 className="text-xl font-bold text-gray-800">주문 상세</h2>
          <p className="mt-1 text-sm text-gray-500">배송형 상품은 직접 배송됩니다</p>
        </div>
      )}

      {/* 배송형 상품이 있는 경우 */}
      {deliveryItems.length > 0 && (
        <div className={couponList.length > 0 ? 'mt-6' : 'mt-4'}>
          <div className="mb-2 flex items-center justify-between px-4">
            <h3 className="text-sm font-bold text-gray-700">배송 상품</h3>
            <span className="text-xs text-gray-400">{deliveryItems.length}건</span>
          </div>
          <div className="space-y-2 px-4">
            {deliveryItems.map((item, index) => (
              <DeliveryRow key={`delivery-${index}`} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* 하단 고정 전달 버튼 - 미전달 쿠폰이 있을 때만 표시 */}
      {unsharedCoupons > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-gray-200 bg-white px-4 py-2">
          <button
            onClick={handleBottomButtonClick}
            className="w-full rounded-xl bg-purple-600 py-4 text-lg font-bold text-white active:bg-purple-700"
          >
            다음 미전달 답례품 전달하기 (미전달 {unsharedCoupons}건)
          </button>
        </div>
      )}

      {/* 안내 모달 */}
      <ShareGuideModal
        open={showShareGuide}
        onConfirm={handleShareGuideConfirm}
        onClose={() => setShowShareGuide(false)}
      />

      {/* 공유 모달 */}
      <GiftCouponShareModal
        openId={shareModalOpenId}
        courtesyContent={courtesyContent}
        linkUrl={currentShareUrl}
        onShare={handleShareComplete}
      />
    </div>
  );
};

export default GiftOrderCouponPage;
